import { Component, inject, OnInit } from '@angular/core';
import { Observable } from 'rxjs';
import { select, Store } from '@ngrx/store';
import { RootState } from '../../../core/state/shared/root-state.interface';
import {
  selectCreateUserErrors,
  selectIsLoading,
} from '../../../core/state/user-session/user-session.selectors';
import {
  FormControl,
  FormGroup,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import Validation from '../../../core/utils/validation';
import { createUser } from '../../../core/state/user-session/user-session.actions';
import { TokenExpirationService } from '../../../core/services/token-expiration.service';
import { ActivatedRoute } from '@angular/router';
import { MessageService } from '../../../core/services/message.service';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { PasswordPattern } from '../../../core/state/password-management/password-management.state';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import {
  MatFormField,
  MatError,
  MatSuffix,
  MatHint,
} from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatCheckbox } from '@angular/material/checkbox';
import { NgClass, AsyncPipe } from '@angular/common';
import { MatButton } from '@angular/material/button';

@Component({
  selector: 'app-create-user-form',
  templateUrl: './create-user-form.component.html',
  styleUrls: [
    './create-user-form.component.scss',
    '../pairing.component.scss',
    '../../login-form/login-form.component.scss',
  ],
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatIcon,
    MatTooltip,
    MatFormField,
    MatInput,
    MatError,
    MatSuffix,
    MatHint,
    MatCheckbox,
    NgClass,
    MatButton,
    AsyncPipe,
    TranslateModule,
  ],
})
export class CreateUserFormComponent implements OnInit {
  createForm: FormGroup;

  showPassword = false;
  showConfirmPassword = false;
  isLoading$: Observable<boolean>;
  token: string;

  private store = inject(Store<RootState>);
  private route = inject(ActivatedRoute);
  private tokenExpirationService = inject(TokenExpirationService);
  private messageService = inject(MessageService);
  private translateService = inject(TranslateService);

  ngOnInit() {
    this.isLoading$ = this.store.pipe(select(selectIsLoading));
    this.token = this.route.snapshot.queryParams['token'];

    this.createForm = new FormGroup(
      {
        username: new FormControl('', [
          Validators.required,
          Validators.minLength(4),
          Validators.maxLength(200),
        ]),
        email: new FormControl('', [
          Validators.required,
          Validators.email,
          Validators.maxLength(200),
        ]),
        displayName: new FormControl('', [Validators.required]),
        password: new FormControl('', [
          Validators.required,
          Validators.minLength(8),
          Validators.pattern(PasswordPattern),
          Validators.maxLength(200),
        ]),
        confirmPassword: new FormControl('', [
          Validators.required,
          Validators.maxLength(200),
        ]),
        agreeToTerms: new FormControl('', [Validators.requiredTrue]),
      },
      {
        validators: [Validation.match('password', 'confirmPassword')],
      },
    );
  }

  get form() {
    return this.createForm.controls;
  }

  wasTouched(fieldName) {
    const control = this.createForm.get(fieldName);
    return control.touched || control.dirty;
  }

  onSubmit() {
    this.tokenExpirationService.clearTokenTimer();
    this.store.dispatch(
      createUser({
        userData: this.createForm.value,
        token: this.token,
      }),
    );

    this.store.select(selectCreateUserErrors).subscribe((error) => {
      if (
        error?.token ||
        (error && !error.username && !error.email && !error.password)
      ) {
        this.messageService.showError({
          title: this.translateService.instant('tokenExpiration.invalidToken'),
          text: this.translateService.instant(
            'tokenExpiration.invalidTokenInfo',
          ),
          buttons: [
            {
              label: this.translateService.instant('tokenExpiration.close'),
              type: 'cancel',
              actionFn: () => window.close(),
            },
          ],
        });
      }
      if (error?.username) {
        this.createForm.get('username').setErrors({ serverError: true });
      }
      if (error?.email) {
        this.createForm.get('email').setErrors({ serverError: true });
      }
      if (error?.password) {
        this.createForm.get('password').setErrors({ serverError: true });
        this.createForm.get('confirmPassword').setErrors({ serverError: true });
      }
    });
  }
}
